import AuthService from "../../base/services/authentication.service";
import { MAX_ASK_HISTORY } from "./helpCenterApi";

/**
 * The Ask AI conversation.
 *
 * The backend stores nothing about it: all the assistant knows of earlier
 * turns is the `history` sent with each question, so the chat lives here. A
 * message is
 *
 *    { id, role: "User" | "Assistant", content, sources?, status }
 *
 * where `status` is "pending" while an answer is on its way, "sent" once it
 * arrived, and "failed" for a question the backend refused together with the
 * bubble holding the refusal. A question and the bubble its answer will fill
 * are added as a pair and settle together, so the two always agree.
 */

/** The turns worth sending: answered ones, oldest first, newest last. */
export const toHistory = (messages = []) =>
   messages
      .filter((message) => message.status === "sent")
      .slice(-MAX_ASK_HISTORY)
      .map(({ role, content }) => ({ role, content }));

/** The history a question was asked with, for asking it again unchanged. */
export const historyBefore = (messages = [], questionId) => {
   const index = messages.findIndex((message) => message.id === questionId);
   return toHistory(index === -1 ? messages : messages.slice(0, index));
};

/** The bubble that holds a question's answer: the message after it. */
export const answerIdOf = (messages = [], questionId) => {
   const index = messages.findIndex((message) => message.id === questionId);
   return index === -1 ? undefined : messages[index + 1]?.id;
};

/** A question, with the bubble its answer will fill. */
export const startQuestion = (messages, { questionId, answerId, content }) => [
   ...messages,
   { id: questionId, role: "User", content, status: "pending" },
   { id: answerId, role: "Assistant", content: "", status: "pending" }
];

/** The same question again, in place, as if it had never failed. */
export const startRetry = (messages, { questionId, answerId }) =>
   messages.map((message) => {
      if (message.id === questionId) return { ...message, status: "pending" };
      if (message.id === answerId) return { ...message, content: "", status: "pending" };
      return message;
   });

/** The answer arrived: the pair counts as a turn and is sent from now on. */
export const settleAnswer = (messages, { questionId, answerId, answer, sources }) =>
   messages.map((message) => {
      if (message.id === questionId) return { ...message, status: "sent" };
      if (message.id === answerId) {
         return { ...message, content: answer ?? "", sources: sources ?? [], status: "sent" };
      }
      return message;
   });

/** The question was refused: neither it nor the refusal is ever sent. */
export const settleError = (messages, { questionId, answerId, error }) =>
   messages.map((message) => {
      if (message.id === questionId) return { ...message, status: "failed" };
      if (message.id === answerId) return { ...message, content: error, status: "failed" };
      return message;
   });

/** The id to give the next message of a chat, restored or new. */
export const nextMessageId = (messages = []) =>
   messages.reduce((last, message) => Math.max(last, message.id || 0), 0) + 1;

// Where the chat is kept between drawer visits: this tab's sessionStorage,
// under the user's own key. Logout empties sessionStorage (AuthService.logout
// -> SessionService.clear), and a chat saved by another user or in another
// organization is dropped rather than shown. Storage that is full, blocked or
// private only means the chat starts empty.

const chatKey = () => {
   const userId = AuthService.getUser()?.id;
   return userId ? `helpCenter.chat.${userId}` : "helpCenter.chat";
};

const currentOrganizationId = () => AuthService.getOrganizationId() ?? null;

/** The conversation this tab left off with, or none. */
export const readChat = () => {
   try {
      const saved = JSON.parse(sessionStorage.getItem(chatKey()));
      if (!saved || !Array.isArray(saved.messages)) return [];
      if (saved.organizationId !== currentOrganizationId()) return [];
      return saved.messages;
   } catch (error) {
      return [];
   }
};

/**
 * Keeps the settled turns. A question still in flight is left out: the answer
 * lands in this tab or nowhere, so a restored "pending" would wait forever.
 */
export const writeChat = (messages = []) => {
   try {
      const settled = messages.filter((message) => message.status !== "pending");
      if (settled.length === 0) {
         sessionStorage.removeItem(chatKey());
         return;
      }
      sessionStorage.setItem(
         chatKey(),
         JSON.stringify({ organizationId: currentOrganizationId(), messages: settled })
      );
   } catch (error) {
      // The chat works without it; it just starts empty next time.
   }
};

/** Forgets the conversation: "New chat", and a change of organization. */
export const clearChat = () => {
   try {
      sessionStorage.removeItem(chatKey());
   } catch (error) {
      // Nothing was stored, so nothing is left to forget.
   }
};
